import type { PointerEvent } from "react";
import { useTranslation } from "react-i18next";
import type { CardResizeSpec, CardSize, GripAxis } from "./cardResizeSpec";
import { usePointerDrag } from "./usePointerDrag";

const clamp = (value: number, min: number, max: number) => Math.max(min, Math.min(max, value));

// Мерено при хващане: клетката на кутийката и вътрешната ширина на
// решетката. Клетката, не самата кутийка — избраният продукт е уголемен
// (`scale`), а клетката е с истинския размер.
const measureCard = (event: PointerEvent<HTMLElement>) => {
  const cell = event.currentTarget.parentElement;
  const grid = cell?.parentElement;
  if (!cell || !grid) return null;
  const gridStyle = getComputedStyle(grid);
  const gridInnerWidth =
    grid.clientWidth - parseFloat(gridStyle.paddingLeft) - parseFloat(gridStyle.paddingRight);
  const rect = cell.getBoundingClientRect();
  return { width: rect.width, height: rect.height, gridInnerWidth };
};

// Кутийките на ред за влачена ширина: най-близкият брой колони, но не
// повече, отколкото се събират по `minWidth`.
const columnsFor = (cardWidth: number, gridInnerWidth: number, spec: CardResizeSpec) => {
  const maxColumns = clamp(
    Math.floor((gridInnerWidth + spec.gap) / (spec.minWidth + spec.gap)),
    1,
    spec.maxColumns,
  );
  return clamp(Math.round((gridInnerWidth + spec.gap) / (cardWidth + spec.gap)), 1, maxColumns);
};

const RESET_BY_AXIS: Record<GripAxis, Partial<CardSize>> = {
  width: { cardColumns: null },
  height: { cardHeight: null },
  both: { cardColumns: null, cardHeight: null },
};

const Grip = ({ axis, spec }: { axis: GripAxis; spec: CardResizeSpec }) => {
  const { t } = useTranslation();
  const { handleProps } = usePointerDrag({
    onStart: measureCard,
    onMove: (start, deltaX, deltaY) => {
      const patch: Partial<CardSize> = {};
      if (axis !== "height") patch.cardColumns = columnsFor(start.width + deltaX, start.gridInnerWidth, spec);
      if (axis !== "width") {
        patch.cardHeight = Math.round(clamp(start.height + deltaY, spec.heightMin, spec.heightMax));
      }
      spec.preview(patch);
    },
    onEnd: spec.commitPreview,
  });

  const label = t(spec.labelKeys[axis]);
  // Нищо не се рисува — подсказва само курсорът.
  return (
    <span
      role="separator"
      aria-orientation={axis === "height" ? "horizontal" : "vertical"}
      aria-label={label}
      title={label}
      onDoubleClick={() => spec.reset(RESET_BY_AXIS[axis])}
      {...handleProps}
      className={`absolute z-10 touch-none select-none ${spec.gripPlacement[axis]}`}
    />
  );
};

/**
 * Дръжките на ИЗБРАНАТА кутийка в решетка с дръжки (масите, продуктите —
 * `CardResizeSpec`): десният ръб — кутийките на ред, долният — височината,
 * ъгълът — двете; ръбовете се хващат по цялата си дължина. Важат за всички
 * кутийки; двоен клик връща по подразбиране. Само на избраната кутийка и
 * НЕВИДИМИ — без черти и точка по ръба (решение 21.10.2026), подсказва само
 * курсорът. Стоят в клетката ДО кутийката, не в нея — кликът по кутийката си
 * е неин (продуктът добавя +1). Докато се влачи, новият размер е само сиви
 * контури (`CardLayoutPreview`) — кутийките го заемат при пускане.
 */
export const CardResizeGrips = ({ spec }: { spec: CardResizeSpec }) => (
  <>
    <Grip axis="width" spec={spec} />
    <Grip axis="height" spec={spec} />
    <Grip axis="both" spec={spec} />
  </>
);
